import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Signup() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);

  const handleGoogleAuth = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Signup failed');
      }

      // Store token in localStorage for session persistence
      if (data.token) {
        localStorage.setItem('session_token', data.token);
      }

      toast.success('Account created successfully!');
      
      // Check if onboarding is needed
      if (!data.onboarding_completed) {
        navigate('/onboarding', { state: { user: data } });
      } else {
        navigate('/dashboard', { state: { user: data } });
      }
    } catch (error) {
      toast.error(error.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1C2127] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-8 animate-fade-in">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white" data-testid="signup-title">
            Create your account
          </h1>
          <p className="text-base text-[#ADB5BD] leading-relaxed">
            Begin your journey to recovery
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-[#252B33] rounded-2xl border border-[#3A424D] shadow-xl p-8 space-y-6">
          {/* Google Sign Up */}
          <Button
            onClick={handleGoogleAuth}
            variant="outline"
            className="w-full rounded-xl px-8 py-6 h-auto text-base font-semibold border-[#3A424D] bg-[#2D3640] text-white hover:bg-[#3A424D] hover:border-[#00B4D8]/50"
            data-testid="google-signup-btn"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#3A424D]"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-[#252B33] text-[#6C757D]">Or sign up with email</span>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium text-[#ADB5BD]">
                Name
              </Label>
              <Input
                id="name"
                type="text"
                placeholder="Your name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="h-12 rounded-xl border-[#3A424D] bg-[#1C2127] text-white placeholder:text-[#6C757D] focus:border-[#00B4D8] focus:ring-[#00B4D8]/20"
                data-testid="signup-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-[#ADB5BD]">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="h-12 rounded-xl border-[#3A424D] bg-[#1C2127] text-white placeholder:text-[#6C757D] focus:border-[#00B4D8] focus:ring-[#00B4D8]/20"
                data-testid="signup-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-[#ADB5BD]">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Create a password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="h-12 rounded-xl border-[#3A424D] bg-[#1C2127] text-white placeholder:text-[#6C757D] focus:border-[#00B4D8] focus:ring-[#00B4D8]/20"
                data-testid="signup-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full px-8 py-6 h-auto text-lg font-bold text-white bg-gradient-to-r from-[#00B4D8] to-[#06D6A0] hover:from-[#06D6A0] hover:to-[#00B4D8] shadow-lg hover:shadow-[#00B4D8]/30 transition-all duration-300"
              data-testid="signup-submit-btn"
            >
              {loading ? 'Creating account...' : 'Create account'}
            </Button>
          </form>
        </div>

        {/* Login Link */}
        <p className="text-center text-sm text-[#ADB5BD]">
          Already have an account?{' '}
          <button
            onClick={() => navigate('/login')}
            className="text-[#00B4D8] font-semibold hover:text-[#06D6A0] transition-colors"
            data-testid="login-link"
          >
            Log in
          </button>
        </p>
      </div>
    </div>
  );
}
